// GenerationStream — the "Quiet Foundry" waiting experience (§V2, from the
// pomela-platform-v2 design spike) while the generation worker streams SSE.
//
// The user's own app assembles piece-by-piece inside a phone frame, in a
// provisional category accent, seeded from their prompt — with an HONEST
// five-bead progress rail gated on REAL pipeline milestones. There is no
// percentage and no timer-driven completion: the beads advance only when
// the corresponding SSE events arrive, the final phase holds indefinitely
// ("the real handoff, not a countdown"), and leaving this screen happens
// only on the true deploy_complete (the state machine unmounts us into the
// editor) or an honest error/stall surface.
//
// All existing state shape and parent handlers (onCancel/onAbort/onRetry)
// are preserved. The component reads the SAME SSE stream, the SAME queue
// notice, and the SAME stall-detection thresholds; only the presentation
// changed.

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { StreamEvent } from '@vibeshell/shared';
import { Check, Eye, Lightbulb, Paperclip, RotateCw, X } from 'lucide-preact';
import type { StreamAttachment } from '../lib/useGenerationSession';
import { summarizePrompt, stripMarkdownLead } from '../lib/summarize-prompt';
import { classifyPrompt } from '../lib/gen-foundry';
import { t } from '../lib/i18n';

interface GenerationStreamProps {
  prompt: string;
  events: StreamEvent[];
  mode: 'quick' | 'deep';
  startedAt: number;
  isRevise?: boolean;
  /**
   * Friendly name from the worker's sidecar `app_name` event. When set,
   * this is the heading — until then, summarizePrompt(prompt) is shown.
   * Defensively cleaned of any leading markdown the model might emit.
   */
  appName?: string;
  /**
   * §A4 — present only while the GenGate holds this gen in the FIFO queue
   * under load. When set, the stream shows an honest "you're in line, ~Xs"
   * notice instead of pretending synthesis has begun. Cleared the instant a
   * slot is granted (the real stages take over).
   */
  queueInfo?: { position: number; etaSeconds: number; queueLength: number };
  /**
   * Honest, render-only echo of the attachments this generation was given —
   * exactly the wire assets the pipeline received (see StreamingState). When
   * present, the wait screen acknowledges them so the creator can see their
   * upload was carried into the build; absent when nothing was attached.
   */
  attachments?: StreamAttachment[];
  /** User-initiated cancel — tears down the stream AND returns to idle. */
  onCancel?: () => void;
  /** Auto-abort on the silence-timeout — tears down the stream, stays mounted. */
  onAbort?: () => void;
  /** Called when the user taps Retry after an auto-abort. */
  onRetry?: () => void;
}

// Stall thresholds — tuned per POMELA_REFACTOR_PLAN.md §5.3 (45s soft
// warning, 180s hard timeout matching the worker-side hard timeout).
const SLOW_THRESHOLD_MS = 45_000;
const TIMEOUT_MS = 180_000;

/** Five spec-pipeline milestones, in order. Mapped from SSE events. */
type StageKey = 'blueprint' | 'hydrate' | 'aesthetic' | 'polish' | 'ready';
const STAGE_KEYS: StageKey[] = ['blueprint', 'hydrate', 'aesthetic', 'polish', 'ready'];

/** Rotating substatus — resolved per-locale via gen.status.0..N. */
const FRIENDLY_STATUS_COUNT = 10;

/** The furthest stage the event stream has reached.
 *
 *  Stage map — rebalanced so the rail actually advances during the long
 *  synthesis phase (which dominates the 60-180s critical path):
 *    blueprint — earliest signals: stream_open, intent, plan_started/progress
 *    hydrate   — plan settled: plan_complete, components_selected, or any
 *                synthesis_progress arrived (the model is streaming tokens)
 *    aesthetic — synthesis_complete: hydration / theming happen here
 *    polish    — build_started/progress/complete + deploy_started: the
 *                HONEST HOLD — everything the model decides is done, the
 *                real deploy is landing. Holds indefinitely.
 *    ready     — deploy_complete (the state machine unmounts us at this
 *                exact event, so 'ready' never actually paints — by design:
 *                the app itself is the celebration). */
function reachedStage(events: StreamEvent[]): StageKey {
  const has = (t: StreamEvent['type']): boolean => events.some((e) => e.type === t);
  if (has('deploy_complete')) return 'ready';
  if (
    has('build_complete') ||
    has('build_started') ||
    has('build_progress') ||
    has('deploy_started')
  )
    return 'polish';
  if (has('synthesis_complete') || has('validation_retry') || has('quality_retry'))
    return 'aesthetic';
  if (
    has('plan_complete') ||
    has('components_selected') ||
    has('synthesis_progress')
  )
    return 'hydrate';
  return 'blueprint';
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );
}

export function GenerationStream({
  prompt,
  events,
  mode,
  startedAt,
  isRevise,
  appName,
  queueInfo,
  attachments,
  onCancel,
  onAbort,
  onRetry
}: GenerationStreamProps) {
  // `mode`, `isRevise` remain part of the public surface.
  void mode;
  void isRevise;

  const stageKey = reachedStage(events);
  const stageIndex = STAGE_KEYS.indexOf(stageKey);
  // The honest hold — the last pre-deploy phase. The phone breathes and the
  // copy says why. Once the bundle is done / the deploy has started, the
  // polish bead ticks done and ONLY the landing bead shimmers (exactly one
  // active bead at any moment — the rail's grammar).
  const holding = stageKey === 'polish';
  const landing =
    holding &&
    events.some((e) => e.type === 'deploy_started' || e.type === 'build_complete');

  // The foundry scene is a pure function of the prompt (stable per mount).
  const scene = useMemo(() => classifyPrompt(prompt), [prompt]);

  // ─── Friendly rotating status (crossfade, reduced-motion aware) ────
  const [statusIndex, setStatusIndex] = useState(0);
  const [statusOpacity, setStatusOpacity] = useState(1);
  useEffect(() => {
    const ROTATE_MS = 3000;
    const FADE_MS = 320;
    const reduceMotion = prefersReducedMotion();
    const tick = window.setInterval(() => {
      if (reduceMotion) {
        setStatusIndex((i) => (i + 1) % FRIENDLY_STATUS_COUNT);
        return;
      }
      setStatusOpacity(0);
      window.setTimeout(() => {
        setStatusIndex((i) => (i + 1) % FRIENDLY_STATUS_COUNT);
        setStatusOpacity(1);
      }, FADE_MS);
    }, ROTATE_MS);
    return () => window.clearInterval(tick);
  }, []);

  // ─── Stall detection (unchanged thresholds; resets on every event) ──
  const [stall, setStall] = useState<'ok' | 'slow' | 'timeout'>('ok');
  const onAbortRef = useRef(onAbort);
  onAbortRef.current = onAbort;
  useEffect(() => {
    setStall('ok');
    const slow = window.setTimeout(() => setStall('slow'), SLOW_THRESHOLD_MS);
    const timeout = window.setTimeout(() => {
      setStall('timeout');
      onAbortRef.current?.();
    }, TIMEOUT_MS);
    return () => {
      window.clearTimeout(slow);
      window.clearTimeout(timeout);
    };
  }, [events.length, startedAt]);

  // Prefer the model-chosen name once the sidecar `app_name` arrives; never
  // let the heading go blank (whitespace prompt + no name yet).
  const cleanedName = appName ? stripMarkdownLead(appName).trim() : '';
  const fellBackToPrompt = !cleanedName;
  const summary = cleanedName || summarizePrompt(prompt) || t('gen.fallbackName');

  // ─── Typed-in phone title ("it's being written for you") ───────────
  // Types the resolved name letter-by-letter into the phone header once the
  // header block has assembled (stage ≥ 1). Reduced motion sets it whole.
  const [typedName, setTypedName] = useState('');
  useEffect(() => {
    if (stageIndex < 1) return;
    if (prefersReducedMotion()) {
      setTypedName(summary);
      return;
    }
    let i = 0;
    setTypedName('');
    const iv = window.setInterval(() => {
      i += 1;
      setTypedName(summary.slice(0, i));
      if (i >= summary.length) window.clearInterval(iv);
    }, 42);
    return () => window.clearInterval(iv);
  }, [summary, stageIndex >= 1]);

  const echo = prompt.trim();

  return (
    <div
      className="gf"
      data-stage={stageIndex}
      data-hold={holding ? '1' : undefined}
      data-category={scene.category}
      style={`--gf-accent:${scene.accent};--app-accent:${scene.accent}`}
    >
      {/* §A4 — honest queue notice (kept verbatim from the previous
          implementation; position/ETA are real GenGate numbers). */}
      {queueInfo && (
        <div role="status" aria-live="polite" className="gf-queue">
          <span aria-hidden="true" className="gen-queue-dot" />
          <span>
            <strong>{t('gen.queueLine')}</strong>
            {queueInfo.etaSeconds > 0
              ? ` · ${
                  queueInfo.etaSeconds < 60
                    ? t('gen.queueEtaSec', { n: Math.max(5, Math.round(queueInfo.etaSeconds / 5) * 5) })
                    : t('gen.queueEtaMin', { n: Math.ceil(queueInfo.etaSeconds / 60) })
                }`
              : ''}
            {queueInfo.position > 1 ? ` · ${t('gen.queuePos', { n: queueInfo.position })}` : ''}
            {'. '}
            {t('gen.queueDemand')}
          </span>
        </div>
      )}

      <div className="genwait" data-genstate={holding ? 'hold' : 'building'}>
        {/* ── The stage: the user's app assembling inside a phone ─────── */}
        <div className="genwait-stage">
          <div className="genwait-bloom" aria-hidden="true" />
          <div className="device" aria-hidden="true" style={`--app-accent:${scene.accent}`}>
            <div className="glass">
            <div className="gf-screen">
              <div className="gf-apphead">
                <span className="gf-appmark" />
                <span className="gf-appname">
                  {typedName || ' '}
                  {stageIndex >= 1 && typedName.length < summary.length && (
                    <i className="gf-caret" />
                  )}
                </span>
              </div>
              <div className="gf-blocks">
                {scene.blocks.map((block, i) => (
                  <FoundryBlockView key={`${block}-${i}`} block={block} order={i} />
                ))}
              </div>
              <div className="gf-tabs">
                {scene.tabs.map((tab, i) => (
                  <span key={tab} className={`gf-tab${i === 0 ? ' is-on' : ''}`}>{tab}</span>
                ))}
              </div>
            </div>
            </div>
          </div>
          {echo && (
            <p className="genwait-prompt" dir="auto">
              {t('gen.echo')} <q>{echo}</q>
            </p>
          )}
          {/* honesty cue — this phone is a layout preview, not the real render */}
          <span className="genwait-previewnote"><Eye size={13} class="icon" aria-hidden="true" /> {t('gen.previewNote')}</span>
        </div>

        {/* ── The status rail: honest progress, no percentages ─────────── */}
        <aside className="genwait-rail">
          <span className="genwait-eyebrow">{t('gen.working')}</span>
          <h2
            key={summary}
            className={`genwait-title cp-prose--fade-in${fellBackToPrompt ? ' genwait-title--pending' : ''}`}
            dir="auto"
          >
            {t('gen.building')} <b>{summary}</b>
          </h2>
          <p className="genwait-status" aria-live="polite" style={{ opacity: statusOpacity }}>
            {t('gen.status.' + statusIndex)}
          </p>

          {/* Honest attachment acknowledgement — reflects ONLY the wire assets
              the pipeline actually received (name + type, plus a sheet's real
              row count; never an invented file size). Absent when nothing was
              attached. */}
          {attachments && attachments.length > 0 && (
            <div
              className="genwait-attach"
              role="group"
              aria-label={t('gen.attachAria')}
            >
              <span className="ga-k">
                <Paperclip size={13} class="icon" aria-hidden="true" />
                {t('gen.attachHeading')}
              </span>
              <ul className="ga-list">
                {attachments.map((a, i) => (
                  <li key={`${a.name}-${i}`} className="ga-item" dir="auto">
                    <span className="ga-name">{a.name}</span>
                    <span className="ga-meta">
                      {t('gen.attachKind.' + a.kind)}
                      {a.rowCount != null ? ` · ${t('gen.attachRows', { n: a.rowCount })}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ol className="genwait-beads" aria-label={t('gen.ariaMilestones')}>
            {STAGE_KEYS.map((sKey, i) => {
              const isDone = i < stageIndex || (landing && i === 3);
              const isActive = landing ? i === 4 : i === stageIndex;
              return (
                <li
                  key={sKey}
                  className="bead"
                  data-bead-state={isDone ? 'done' : isActive ? 'active' : 'upcoming'}
                >
                  <span className="bead-dot" aria-hidden="true">
                    {isDone && <Check size={12} strokeWidth={3.2} class="icon" />}
                  </span>
                  <span className="bead-lbl">{t('gen.stage.' + sKey)}</span>
                </li>
              );
            })}
          </ol>

          {/* While-you-wait tip — rotates with the status index. */}
          <div className="genwait-tip">
            <span className="gt-ic"><Lightbulb size={16} class="icon" aria-hidden="true" /></span>
            <div>
              <span className="gt-k">{t('gen.tipK')}</span>
              <p>{t('gen.tip.' + (statusIndex % 4))}</p>
            </div>
          </div>

          {/* The honest hold — visible only while waiting on the real
              deploy. Three accent pips cycle; the copy tells the truth. */}
          {holding && <div className="genwait-pips" aria-hidden="true"><i /><i /><i /></div>}
          <p className="genwait-reassure" role={holding ? 'status' : undefined}>
            {holding ? t('gen.hold') : t('gen.reassure')}
          </p>
        </aside>
      </div>

      {/* Stall handling — UX preserved from the previous implementation. */}
      {stall === 'slow' && (
        <div role="status" className="gen-alert gen-alert--warn">
          <span className="gen-alert__text">{t('gen.slow')}</span>
          {onCancel && (
            <button type="button" onClick={onCancel} className="gen-alert__btn">
              <X size={12} />
              {t('gen.cancel')}
            </button>
          )}
        </div>
      )}
      {stall === 'timeout' && (
        <div role="alert" className="gen-alert gen-alert--error">
          <span className="gen-alert__text">{t('gen.timeout')}</span>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="gen-alert__btn gen-alert__btn--solid"
            >
              <RotateCw size={12} />
              {t('gen.retry')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** One assembling block inside the phone. Pure markup — which blocks show
 *  (and when) is driven by CSS stage gates on the .gf[data-stage] host;
 *  `order` staggers the entrance within a stage. */
function FoundryBlockView({ block, order }: { block: string; order: number }) {
  const style = `--gf-i:${order}`;
  switch (block) {
    case 'slots':
      return (
        <div className="gf-block gf-slots" style={style}>
          <i /><i /><i /><i /><i /><i />
        </div>
      );
    case 'stats':
      return (
        <div className="gf-block gf-stats" style={style}>
          <i /><i /><i />
        </div>
      );
    case 'rsvp':
      return (
        <div className="gf-block gf-rsvp" style={style}>
          <b /><i /><i />
        </div>
      );
    case 'products':
      return (
        <div className="gf-block gf-products" style={style}>
          <i /><i /><i /><i />
        </div>
      );
    case 'cards':
      return (
        <div className="gf-block gf-cards" style={style}>
          <i /><i />
        </div>
      );
    case 'rows':
      return (
        <div className="gf-block gf-rows" style={style}>
          <i /><i /><i />
        </div>
      );
    case 'hero':
    default:
      return <div className="gf-block gf-hero" style={style} />;
  }
}
